import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import "../Styles/ChequeTracker.css";

import NewChequeModal from "../Components/NewChequeModal";

import api from "../api";

import * as XLSX from "xlsx";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { toast } from "react-toastify";

import CountUp from "react-countup";


import {
  Search,
  Plus,
  FileSpreadsheet,
  FileText,
  Wallet,
  Clock3,
  CheckCircle2,
  XCircle,
  Landmark,
  ArrowRight,
  Bell,
  CircleDollarSign,
  ShieldCheck,
} from "lucide-react";

const ChequeTracker = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");

  const [isModalOpen, setIsModalOpen] =
    useState(false);

  const [cheques, setCheques] = useState([]);

  const [startDate, setStartDate] =
    useState("");

  const [endDate, setEndDate] =
    useState("");

  const [selectedMonth, setSelectedMonth] =
    useState(() => {
      const today = new Date();

      return `${today.getFullYear()}-${String(
        today.getMonth() + 1
      ).padStart(2, "0")}`;
    });

const [showAll, setShowAll] =
  useState(false);


  const fetchCheques = async () => {
    try {
      const data = await api.getCheques();

      setCheques(data);
    } catch (error) {
      console.error(
        "Error fetching cheques:",
        error
      );
    }
  };

  useEffect(() => {
    fetchCheques();
  }, []);

  const handleSaveCheque = async (
    newCheque
  ) => {
    const savedCheque =
      await api.createCheque(newCheque);

    if (savedCheque) {
      setCheques((prev) => [
        ...prev,
        savedCheque,
      ]);

      toast.success(
        "Cheque added successfully!"
      );
    }
  };

  const filteredCheques = cheques.filter(
    (cheque) => {
      const search =
        searchTerm.toLowerCase();

      return (
        cheque.cheque_number
          ?.toString()
          .toLowerCase()
          .includes(search) ||
        cheque.payer
          ?.toLowerCase()
          .includes(search) ||
        cheque.payee
          ?.toLowerCase()
          .includes(search)
      );
    }
  );

  const monthlyCheques =
    filteredCheques.filter((cheque) => {
      if (!cheque.date_posted)
        return false;

      const chequeDate = new Date(
        cheque.date_posted
      );

      const chequeMonth = `${chequeDate.getFullYear()}-${String(
        chequeDate.getMonth() + 1
      ).padStart(2, "0")}`;

      return chequeMonth === selectedMonth;
    });


const displayedCheques =
  showAll
    ? monthlyCheques
    : monthlyCheques.slice(0, 20);

  const handleStatusChange = async (
    chequeNumber,
    newStatus
  ) => {
    const updatedCheque =
      await api.updateChequeStatus(
        chequeNumber,
        newStatus
      );

    if (updatedCheque) {
      setCheques((prev) =>
        prev.map((cheque) =>
          cheque.cheque_number ===
          chequeNumber
            ? {
                ...cheque,
                status: newStatus,
              }
            : cheque
        )
      );

      toast.success(
        "Status updated successfully!"
      );
    }
  };

  const exportReport = () => {
    if (!filteredCheques.length) {
      toast.warning(
        "No cheque data available."
      );

      return;
    }

    const worksheet =
      XLSX.utils.json_to_sheet(
        filteredCheques
      );

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Cheque Report"
    );

    XLSX.writeFile(
      workbook,
      `Cheque_Report.xlsx`
    );
  };

  const exportPDF = () => {
    const doc = new jsPDF();

    const tableData =
      monthlyCheques.map((cheque) => [
        cheque.cheque_number,
        cheque.payer,
        cheque.payee,
        cheque.amount,
        cheque.status,
      ]);

    autoTable(doc, {
      head: [
        [
          "Cheque #",
          "Payer",
          "Payee",
          "Amount",
          "Status",
        ],
      ],
      body: tableData,
    });

    doc.save("Cheque_Report.pdf");
  };

  const totalCheques =
    monthlyCheques.length;

  const totalValue =
    monthlyCheques.reduce(
      (sum, cheque) =>
        sum +
        parseFloat(cheque.amount || 0),
      0
    );

  const totalAdminCharges =
    monthlyCheques.reduce(
      (sum, cheque) =>
        sum +
        parseFloat(
          cheque.admin_charge || 0
        ),
      0
    );

  const depositedValue =
    monthlyCheques
      .filter(
        (c) => c.status === "Deposited"
      )
      .reduce(
        (sum, cheque) =>
          sum +
          parseFloat(
            cheque.amount || 0
          ),
        0
      );

  const clearedValue =
    monthlyCheques
      .filter(
        (c) => c.status === "Cleared"
      )
      .reduce(
        (sum, cheque) =>
          sum +
          parseFloat(
            cheque.amount || 0
          ),
        0
      );

  const cancelledValue =
    monthlyCheques
      .filter(
        (c) => c.status === "Cancelled"
      )
      .reduce(
        (sum, cheque) =>
          sum +
          parseFloat(
            cheque.amount || 0
          ),
        0
      );

const unpresentedValue =
  monthlyCheques
    .filter(
      (c) =>
        c.status ===
        "Unpresented"
    )
    .reduce(
      (sum, cheque) =>
        sum +
        parseFloat(
          cheque.amount || 0
        ),
      0
    );


  return (
    <div className="cheque-page">

      {isModalOpen && (
        <NewChequeModal
          onClose={() =>
            setIsModalOpen(false)
          }
          onSave={handleSaveCheque}
        />
      )}

      {/* HERO */}

      <div className="cheque-hero">

        <div className="hero-content">

          <div className="hero-pill">
            <Landmark size={15} />
            CHEQUE TRACKER
          </div>

          <h1>
            Track Every Cheque.
            <span>
              Stay In Control.
            </span>
          </h1>

          <p>
            Monitor cheque activity,
            reconciliation, payments,
            clearances and financial
            operations across your
            organization in real-time.
          </p>

          <div className="hero-buttons">

            <button
              className="hero-btn primary"
              onClick={() =>
                setIsModalOpen(true)
              }
            >
              <Plus size={18} />
              Add New Cheque
            </button>

            <button
              className="hero-btn secondary"
            >
              View Reports
            </button>

          </div>

        </div>

        <div className="hero-visual">

<div className="floating-cheque">

  {/* BACK GLOW */}

  <div className="cheque-glow"></div>

  {/* BACK CHEQUE */}

  <div className="cheque-layer back"></div>

  {/* MIDDLE CHEQUE */}

  <div className="cheque-layer middle"></div>

  {/* MAIN CHEQUE */}

  <div className="cheque-card">

    <div className="cheque-top">

      <div>
        <h4>RCS FINANCE</h4>
        <span>
          Cheque Management System
        </span>
      </div>

      <div className="cheque-date">
        MAY 2026
      </div>

    </div>

    <div className="pay-section">

      <span>
        PAY TO:
      </span>

      <h2>
        John
      </h2>

    </div>

    <div className="amount-row">

      <span>
        Amount
      </span>

      <h1>
        $10,000.00
      </h1>

    </div>

    <div className="cheque-footer">

      <div className="signature-line">

        <span>
          Authorized Signature
        </span>

        <h3>
          RCS Finance
        </h3>

      </div>

      <div className="security-chip"></div>

    </div>

  </div>

  {/* FLOATING STATUS PILLS */}

  <div className="float-pill cleared">
    ✓ Cleared
  </div>

  <div className="float-pill deposited">
    ⏳ Deposited
  </div>

  <div className="float-pill cancelled">
    ✕ Cancelled
  </div>

</div>
        </div>

      </div>

      {/* ANALYTICS */}

<div className="analytics-grid">

  {/* TOTAL CHEQUES */}

  <div className="analytics-card">

    <div className="analytics-icon blue">
      <Wallet size={22} />
    </div>

    <div>

      <h4>Total Cheques</h4>

      <p>
        <CountUp
          end={totalCheques}
          duration={1.2}
        />
      </p>

    </div>

  </div>

  {/* TOTAL VALUE */}

  <div className="analytics-card">

    <div className="analytics-icon green">
      <CircleDollarSign size={22} />
    </div>

    <div>

      <h4>Total Value</h4>

      <p
        className={
          String(totalValue).length > 10
            ? "extra-small-value"
            : String(totalValue).length > 7
            ? "small-value"
            : ""
        }
      >
        $
        <CountUp
          end={totalValue}
          separator=","
          duration={1.5}
        />
      </p>

    </div>

  </div>


{/* ADMIN CHARGES */}

<div className="analytics-card">

  <div className="analytics-icon purple">
    <ShieldCheck size={22} />
  </div>

  <div>

    <h4>Admin Charges</h4>

    <p
      className={
        String(totalAdminCharges).length > 10
          ? "extra-small-value"
          : String(totalAdminCharges).length > 7
          ? "small-value"
          : ""
      }
    >
      $
      <CountUp
        end={totalAdminCharges}
        separator=","
        duration={1.5}
      />
    </p>

  </div>

</div>


  {/* UNPRESENTED */}

  <div className="analytics-card">

    <div className="analytics-icon blue">
      <Wallet size={22} />
    </div>

    <div>

      <h4>Unpresented</h4>

      <p
        className={
          String(unpresentedValue)
            .length > 10
            ? "extra-small-value"
            : String(
                unpresentedValue
              ).length > 7
            ? "small-value"
            : ""
        }
      >
        $
        <CountUp
          end={unpresentedValue}
          separator=","
          duration={1.5}
        />
      </p>

    </div>

  </div>

  {/* DEPOSITED */}

  <div className="analytics-card">

    <div className="analytics-icon orange">
      <Clock3 size={22} />
    </div>

    <div>

      <h4>Deposited</h4>

      <p
        className={
          String(depositedValue)
            .length > 10
            ? "extra-small-value"
            : String(
                depositedValue
              ).length > 7
            ? "small-value"
            : ""
        }
      >
        $
        <CountUp
          end={depositedValue}
          separator=","
          duration={1.5}
        />
      </p>

    </div>

  </div>

  {/* CLEARED */}

  <div className="analytics-card">

    <div className="analytics-icon teal">
      <CheckCircle2 size={22} />
    </div>

    <div>

      <h4>Cleared</h4>

      <p
        className={
          String(clearedValue)
            .length > 10
            ? "extra-small-value"
            : String(
                clearedValue
              ).length > 7
            ? "small-value"
            : ""
        }
      >
        $
        <CountUp
          end={clearedValue}
          separator=","
          duration={1.5}
        />
      </p>

    </div>

  </div>

  {/* CANCELLED */}

  <div className="analytics-card">

    <div className="analytics-icon red">
      <XCircle size={22} />
    </div>

    <div>

      <h4>Cancelled</h4>

      <p
        className={
          String(cancelledValue)
            .length > 10
            ? "extra-small-value"
            : String(
                cancelledValue
              ).length > 7
            ? "small-value"
            : ""
        }
      >
        $
        <CountUp
          end={cancelledValue}
          separator=","
          duration={1.5}
        />
      </p>

    </div>

  </div>

</div>

      {/* FILTERS */}

      <div className="filters-wrapper">

        <div className="search-wrapper">

          <Search size={18} />

          <input
            type="text"
            placeholder="Search cheques..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />

        </div>

        <input
          type="month"
          value={selectedMonth}
          onChange={(e) =>
            setSelectedMonth(
              e.target.value
            )
          }
          className="modern-input"
        />

        <input
          type="date"
          value={startDate}
          onChange={(e) =>
            setStartDate(
              e.target.value
            )
          }
          className="modern-input"
        />

        <input
          type="date"
          value={endDate}
          onChange={(e) =>
            setEndDate(
              e.target.value
            )
          }
          className="modern-input"
        />

        <button
          className="export-btn excel"
          onClick={exportReport}
        >
          <FileSpreadsheet
            size={18}
          />
          Excel
        </button>

        <button
          className="export-btn pdf"
          onClick={exportPDF}
        >
          <FileText size={18} />
          PDF
        </button>

      </div>

      {/* DASHBOARD */}

      <div className="dashboard-grid">

        {/* TABLE */}

        <div className="table-card">

          <div className="table-header">

            <h2>
              Recent Cheques
            </h2>

<button
  onClick={() =>
    setShowAll(!showAll)
  }
>
{showAll
  ? "Collapse Table"
  : "View All"}
</button>

          </div>

         <div
  className={`table-wrapper ${
    showAll &&
    monthlyCheques.length > 30
      ? "scrollable"
      : ""
  }`}
>

            <table className="modern-table">

              <thead>

                <tr>
                  <th>Cheque #</th>
                  <th>Payer</th>
                  <th>Payee</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>

              </thead>

              <tbody>

                {monthlyCheques.length >
                0 ? (
                  displayedCheques.map(
                    (
                      cheque,
                      index
                    ) => (

                      <tr
                        key={index}
                        onClick={() =>
                          navigate(
                            `/cheque-details/${cheque.cheque_number}`,
                            {
                              state:
                                {
                                  cheque,
                                },
                            }
                          )
                        }
                      >

<td>

  <div className="cheque-number-alert-wrap">

    <span>
      {cheque.cheque_number}
    </span>

{cheque.notes &&
 cheque.notes.trim() !== "" && (

      <div className="cheque-noted-alert">

        <Bell size={12} />

        ALERT!

      </div>

    )}

  </div>

</td>

                        <td>
                          {
                            cheque.payer
                          }
                        </td>

                        <td>
                          {
                            cheque.payee
                          }
                        </td>

                        <td>
                          $
                          {Number(
                            cheque.amount
                          ).toLocaleString()}
                        </td>

                        <td>
                          {new Date(
                            cheque.date_posted
                          ).toLocaleDateString()}
                        </td>

                        <td>

<div
  className="cheque-tracker-status-wrapper"
  onClick={(e) =>
    e.stopPropagation()
  }
>

  <div
    className={`cheque-tracker-status-pill ${cheque.status.toLowerCase()}`}
  >

    <span>
      {cheque.status}
    </span>

  </div>

  <div className="cheque-tracker-status-menu">

    <button
      className="unpresented"
      onClick={() =>
        handleStatusChange(
          cheque.cheque_number,
          "Unpresented"
        )
      }
    >
      Unpresented
    </button>

    <button
      className="deposited"
      onClick={() =>
        handleStatusChange(
          cheque.cheque_number,
          "Deposited"
        )
      }
    >
      Deposited
    </button>

    <button
      className="cleared"
      onClick={() =>
        handleStatusChange(
          cheque.cheque_number,
          "Cleared"
        )
      }
    >
      Cleared
    </button>

    <button
      className="cancelled"
      onClick={() =>
        handleStatusChange(
          cheque.cheque_number,
          "Cancelled"
        )
      }
    >
      Cancelled
    </button>

  </div>

</div>
                        </td>

                      </tr>
                    )
                  )
                ) : (
                  <tr>

                    <td
                      colSpan="6"
                      className="empty-state"
                    >
                      No cheques
                      found.
                    </td>

                  </tr>
                )}

              </tbody>

            </table>

          </div>

        </div>

        {/* SIDEBAR */}

        <div className="sidebar-column">

          <div className="sidebar-card">

            <h3>
              Quick Actions
            </h3>

<div className="sidebar-card">

  <h3>
    Quick Actions
  </h3>

  <button
    onClick={() => setIsModalOpen(true)}
  >
    <Plus size={16} />
    Add Cheque
    <ArrowRight size={16} />
  </button>

  <button
    onClick={exportReport}
  >
    <FileSpreadsheet size={16} />
    Export Report
    <ArrowRight size={16} />
  </button>

  <button
    onClick={exportPDF}
  >
    <FileText size={16} />
    Export PDF
    <ArrowRight size={16} />
  </button>

<button
    onClick={() => navigate("/bank-reconciliation")}
>
    <Landmark size={16} />
    Bank Reconciliation
    <ArrowRight size={16} />
</button>

</div>

          </div>

<div className="sidebar-card">

  <h3>
    Status Breakdown
  </h3>

  <div className="status-row">

    <div className="status-info">

      <div className="mini-status-icon cleared">
        <CheckCircle2 size={14} />
      </div>

      <span>
        Cleared
      </span>

    </div>

    <strong className="status-value cleared-text">

      $
      <CountUp
        end={clearedValue}
        separator=","
        duration={1.2}
      />

    </strong>

  </div>
<div className="status-row">

  <div className="status-info">

    <div className="mini-status-icon unpresented">

      <Wallet size={14} />

    </div>

    <span>
      Unpresented
    </span>

  </div>

  <strong className="status-value unpresented-text">

    $
    <CountUp
      end={unpresentedValue}
      separator=","
      duration={1.2}
    />

  </strong>

</div>
  <div className="status-row">

    <div className="status-info">

      <div className="mini-status-icon deposited">
        <Clock3 size={14} />
      </div>

      <span>
        Deposited
      </span>

    </div>

    <strong className="status-value deposited-text">

      $
      <CountUp
        end={depositedValue}
        separator=","
        duration={1.2}
      />

    </strong>

  </div>

  <div className="status-row">

    <div className="status-info">

      <div className="mini-status-icon cancelled">
        <XCircle size={14} />
      </div>

      <span>
        Cancelled
      </span>

    </div>

    <strong className="status-value cancelled-text">

      $
      <CountUp
        end={cancelledValue}
        separator=","
        duration={1.2}
      />

    </strong>

  </div>

</div>

          <div className="notification-card">

            <div className="notification-top">

              <Bell size={20} />

              <h3>
                Stay Updated
              </h3>

            </div>

            <p>
              Get real-time alerts
              on cheque clearances,
              due dates and payment
              updates.
            </p>

            <button>
              Enable
              Notifications
            </button>

          </div>

        </div>

      </div>

    </div>
  );
};

export default ChequeTracker;